/**
 * AdditionsLine — candidates added per WEEK, as an SVG area + line.
 *
 * WEEKLY SINCE 2026-08-27, AND WITH NO CONTROL TO CHANGE IT. Jared, 2026-08-25:
 * *"this one, as we talked about, should be weekly, not monthly"*, and when Jose
 * asked whether the reader should be able to switch: *"No, you can just make it
 * by week and no unchangeable for now."* The rollup is therefore a property of
 * the series the backend composes (`additions_by_week`, report schema 3) rather
 * than of a picker this component would own — a month is not divisible into
 * weeks after the fact, so a client-side switch was never available anyway.
 *
 * The backend hands over a CONTIGUOUS series with zero-filled gaps, which is what
 * makes a line chart honest here: plotting only the weeks that had activity
 * would draw a straight run between two distant points and imply steady work
 * through a quiet stretch that never happened.
 *
 * A single-point series degenerates (no line to draw), so it renders as a lone
 * marker plus its label rather than an empty panel — the shape a brand-new search
 * has in its first week.
 *
 * NO SERIES TOGGLE, deliberately. FR2's audit covers every chart that draws more
 * than one series; this one draws exactly one, and a toggle over a single series
 * is a button whose only function is to blank the chart.
 *
 * The hover crosshair IS here, though, and for the same reason it is on the
 * other two line charts: the per-week figure exists nowhere else on the page.
 * The summary below names only the peak, so "how many did we add that week" is
 * otherwise a question the reader answers by squinting at a y-axis with one
 * labelled value on it.
 */

import type { SearchReportWeek } from "@nu-assist/contracts";
import { useMemo } from "react";

import { ChartTooltip } from "./ChartTooltip";
import { SeriesSwatch } from "./SeriesSwatch";
import { dayLabel, monthLabel } from "./dayAxis";
import { FALLBACK_CHART_WIDTH, useElementWidth } from "./useElementWidth";
import { type PlotGeometry, useNearestPoint } from "./useNearestPoint";

export interface AdditionsLineProps {
  weeks: readonly SearchReportWeek[];
  /** Shown when nothing is plottable. */
  emptyMessage: string;
  /**
   * False renders a picture instead of an instrument: no focusable surface, no
   * crosshair, no tooltip, no live region, and a legend of plain text. For the
   * downloaded report, which runs no JavaScript — every one of those affordances
   * would be inert markup inviting an interaction that cannot happen.
   *
   * The per-point figures are the thing a static chart genuinely loses. The
   * legend still carries each series' window total, and the document's tables
   * carry the rest.
   */
  interactive?: boolean;
  /**
   * Overrides the measured width.
   *
   * `useElementWidth` measures in an effect, and `renderToStaticMarkup` runs no
   * effects — so a static render would fall back to `FALLBACK_CHART_WIDTH` and then
   * scale by `containerWidth / 480` in whatever browser opened the file,
   * re-creating the very font-scaling bug the measured width exists to fix. The
   * export passes the width its layout actually uses.
   */
  width?: number;
}

const HEIGHT = 180;
const PAD_LEFT = 8;
const PAD_RIGHT = 8;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
const PLOT_HEIGHT = HEIGHT - PAD_TOP - PAD_BOTTOM;

export function AdditionsLine({
  weeks,
  emptyMessage,
  interactive = true,
  width: widthOverride,
}: AdditionsLineProps) {
  const surface = useElementWidth(FALLBACK_CHART_WIDTH);
  // `widthOverride` wins over the measurement: a static render has no layout to
  // measure and `renderToStaticMarkup` runs no effects, so the export passes the
  // width its own document uses. Live pages pass nothing and measure.
  const width = widthOverride ?? surface.width;
  // Memoized on the measured width — `useNearestPoint` keys its callbacks on
  // this object's identity. No `height`: a single-series chart has no nearest
  // series to pick, so it asks for no pointer y.
  const geometry = useMemo<PlotGeometry>(
    () => ({ width, padLeft: PAD_LEFT, padRight: PAD_RIGHT }),
    [width]
  );
  const crosshair = useNearestPoint(weeks.length, geometry);
  const plotWidth = width - PAD_LEFT - PAD_RIGHT;

  if (weeks.length === 0) {
    return (
      <p data-testid="additions-empty" className="text-body text-text-secondary">
        {emptyMessage}
      </p>
    );
  }

  const peak = Math.max(...weeks.map((w) => w.count), 1);
  const step = weeks.length > 1 ? plotWidth / (weeks.length - 1) : 0;
  const points = weeks.map((week, i) => ({
    week,
    x: PAD_LEFT + i * step + (weeks.length > 1 ? 0 : plotWidth / 2),
    y: PAD_TOP + PLOT_HEIGHT - (week.count / peak) * PLOT_HEIGHT,
  }));

  const line = points.map((p) => `${p.x},${p.y}`).join(" ");
  const area = `${PAD_LEFT},${PAD_TOP + PLOT_HEIGHT} ${line} ${
    points[points.length - 1].x
  },${PAD_TOP + PLOT_HEIGHT}`;

  // Label every point on a short series, then thin the ticks as it gets dense,
  // so the axis does not turn into a smear. WEEKS ARE FOUR TIMES DENSER THAN
  // THE MONTHS THIS CHART USED TO PLOT — a year is 52 points, not 12 — so the
  // ladder needed a rung above the old `> 14 -> every third`: at 52 points that
  // would still print 18 overlapping labels.
  const labelEvery =
    weeks.length > 40
      ? 13
      : weeks.length > 20
        ? 6
        : weeks.length > 14
          ? 3
          : weeks.length > 8
            ? 2
            : 1;

  const focused = interactive ? crosshair.index : null;

  return (
    <div data-testid="additions-line">
      {/* THE FOCUSABLE SURFACE. Two suppressions below, one prose reason for
          both: this div is the chart's interactive surface, and biome has no
          rule that knows charts exist.

          `noNoninteractiveTabindex` — the per-week figures are in no other DOM
          node on the page, so a chart that cannot take focus withholds them from
          every reader who does not use a pointer (FR1.5). `@nu-assist/ui`'s
          `Tooltip` makes the same call for the same reason.

          `useSemanticElements` — the element biome wants for `role="group"` is
          `<fieldset>`, which announces its contents as a grouping of form
          controls and pulls `<legend>` semantics in with it. There is no
          semantic element for "a plot you walk with the arrow keys"; the role,
          the label and the key handling below ARE the accessible-chart
          pattern. */}
      <div
        // NOTE: these two used to carry `biome-ignore` directives. They stopped
        // being needed when `tabIndex` and `role` became EXPRESSIONS — biome's a11y
        // rules only fire on literals. The reasoning is kept because it is still
        // true, and because hardcoding either value again brings the rules back.
        // the chart IS the interactive surface — see the note above
        tabIndex={interactive ? 0 : undefined}
        // <fieldset> would announce a chart as a form-control group — see the note above
        role={interactive ? "group" : undefined}
        aria-label="Candidates added by week. Use the left and right arrow keys to read each week, Home and End for the ends."
        data-testid="additions-surface"
        ref={surface.ref}
        className="relative rounded-xs focus:outline-none focus:ring-1 focus:ring-accent"
        {...(interactive ? crosshair.surfaceProps : {})}
      >
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          role="presentation"
          aria-hidden="true"
          className="block w-full"
        >
          {/* Behind everything, so the area fill and the line stay legible. */}
          {focused !== null ? (
            <line
              x1={points[focused].x}
              y1={PAD_TOP}
              x2={points[focused].x}
              y2={PAD_TOP + PLOT_HEIGHT}
              className="stroke-text-secondary"
              strokeWidth={0.75}
              strokeDasharray="2 2"
            />
          ) : null}
          {weeks.length > 1 ? <polygon points={area} className="fill-chart-1 opacity-15" /> : null}
          {weeks.length > 1 ? (
            <polyline
              points={line}
              fill="none"
              strokeWidth={2}
              strokeLinejoin="round"
              className="stroke-chart-1"
            />
          ) : null}
          {points.map((p, i) => (
            <circle
              key={p.week.week}
              cx={p.x}
              cy={p.y}
              r={focused === i ? 4.5 : 3}
              className="fill-chart-1"
            />
          ))}
          {points.map((p, i) =>
            i % labelEvery === 0 ? (
              <text
                key={`label-${p.week.week}`}
                x={p.x}
                y={HEIGHT - 8}
                // Outer ticks anchor to the PLOT, not to their own centre.
                // The first point sits at PAD_LEFT (8) in a 480-unit viewBox,
                // and January's label carries a year ("Jan 26"), so a centred
                // first tick overflows the left edge and renders clipped as
                // "an 26" — reported on real data whose first day is
                // 2026-01-30. Found by /pr-review iter 1, cause pinned at
                // iter 2.
                textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
                className="fill-text-secondary text-nano"
              >
                {monthLabel(p.week.week)}
              </text>
            ) : null
          )}
        </svg>

        {focused !== null ? (
          <ChartTooltip
            testId="additions-tooltip"
            title={`Week of ${dayLabel(weeks[focused].week)}`}
            rows={[
              {
                key: "added",
                label: "Added",
                // Printed even when it is 0: the series is zero-filled so a
                // quiet week reads as quiet, and a tooltip that went blank
                // there would put the reader back to guessing.
                value: String(weeks[focused].count),
                swatch: <SeriesSwatch strokeClass="stroke-chart-1" />,
              },
            ]}
            side={crosshair.side}
          />
        ) : null}
      </div>

      {/* The keyboard reader's copy of the crosshair — fed only on a key press. */}
      <output data-testid="additions-readout" className="sr-only">
        {focused !== null && crosshair.fromKeyboard
          ? `Week of ${dayLabel(weeks[focused].week)}: ${weeks[focused].count} added`
          : ""}
      </output>

      {/* The accessible representation — the SVG is decorative. Kept compact
          because a 104-week series would otherwise dominate the panel. */}
      <p data-testid="additions-summary" className="mt-2 text-caption text-text-secondary">
        {weeks.length} week{weeks.length === 1 ? "" : "s"}, peak{" "}
        <strong className="text-text">{peak}</strong> in the week of{" "}
        {dayLabel(weeks.find((w) => w.count === peak)?.week ?? weeks[0].week)}
      </p>
    </div>
  );
}
